import axios from 'axios';

declare global {
  interface Window {
    __SIMULATORO_CONFIG__?: {
      apiUrl?: string;
    };
  }
}

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

function ensureTrailingSlash(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

function normalizeBaseUrl(candidate: string): string {
  const trimmed = candidate.trim();

  if (trimmed.length === 0) {
    return trimmed;
  }

  const sameOriginPrefixes = ['/v1', 'v1', './v1'];
  const isAbsoluteUrl =
    /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmed) || trimmed.startsWith('//');

  if (!isAbsoluteUrl && sameOriginPrefixes.some(prefix => trimmed === prefix || trimmed.startsWith(`${prefix}/`))) {
    const normalized = trimmed.replace(/^\/?(?:\.\/)?v1/, '/api/v1');
    const ensured = normalized.startsWith('/') ? normalized : `/${normalized}`;
    return ensureTrailingSlash(ensured);
  }

  const shouldNormalizeSameOriginPathname = (url: URL) =>
    sameOriginPrefixes.some(
      prefix => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
    );

  try {
    const url = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : undefined);
    const isSameOrigin =
      typeof window === 'undefined' || url.origin === window.location.origin;

    if (isSameOrigin && shouldNormalizeSameOriginPathname(url)) {
      url.pathname = url.pathname.replace(/^\/?v1/, '/api/v1');
    }
    return ensureTrailingSlash(url.toString());
  } catch (error) {
    return ensureTrailingSlash(trimmed);
  }
}

function shouldUseCandidate(candidate: string): boolean {
  if (typeof window === 'undefined') return true;

  try {
    const resolved = new URL(candidate, window.location.origin);
    const candidateHost = resolved.hostname.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();

    const candidateIsLocal = LOCAL_HOSTNAMES.has(candidateHost);
    const runningLocally = LOCAL_HOSTNAMES.has(currentHost);

    if (candidateIsLocal && !runningLocally) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}

function resolveBaseUrl(): string {
  const runtimeCandidate =
    typeof window !== 'undefined' ? window.__SIMULATORO_CONFIG__?.apiUrl : undefined;
  const envCandidate = process.env.REACT_APP_API_URL;

  const candidates = [runtimeCandidate, envCandidate].filter(
    (value): value is string => Boolean(value && value.trim().length > 0),
  );

  for (const candidate of candidates) {
    if (shouldUseCandidate(candidate)) {
      return normalizeBaseUrl(candidate.trim());
    }
  }

  return '/api/v1/';
}

const baseURL = resolveBaseUrl();

const api = axios.create({
  baseURL,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;
